import { aiAssistantRoutes } from './prototypes/easm-platform/ai-assistant/ai-assistant.routes';
import { Routes } from '@angular/router';
import { AuthGuard, AuthLogoutGuard, RouterTools, SolErrorRoutePageComponent } from '@sol/components';
import { PrototypeRouteId } from './models/prototype-route-id';

export const routes: Routes = [
  // Home
  RouterTools.ofRedirect(PrototypeRouteId.app_prototype, PrototypeRouteId.home),
  RouterTools.ofLazy(PrototypeRouteId.home, () =>
    import('./prototypes/home/home-page.component').then(m => m.HomePageComponent),
  ),

  // Billing prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_billing, PrototypeRouteId.billing_partner_dashboard),
  RouterTools.ofRedirect(PrototypeRouteId.billing_partner, PrototypeRouteId.billing_partner_dashboard),
  RouterTools.ofRedirect(PrototypeRouteId.billing_partner_usage, PrototypeRouteId.billing_partner_usage_billing),
  RouterTools.ofRedirect(PrototypeRouteId.billing_admin_usage, PrototypeRouteId.billing_admin_usage_billing),
  RouterTools.ofRedirect(
    PrototypeRouteId.billing_partner_usage_billing,
    PrototypeRouteId.billing_partner_usage_billing_overview,
  ),
  RouterTools.ofRedirect(
    PrototypeRouteId.billing_admin_usage_billing,
    PrototypeRouteId.billing_admin_usage_billing_overview,
  ),

  RouterTools.ofLazy(PrototypeRouteId.billing_partner_dashboard, () =>
    import('./prototypes/billing/pages/partner-dashboard-page/partner-dashboard-page.component').then(
      m => m.PartnerDashboardPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.billing_partner_scope, () =>
    import('./prototypes/billing/pages/partner-scope-page/partner-scope-page.component').then(
      m => m.PartnerScopePageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.billing_partner_usage_billing_overview, () =>
    import('./prototypes/billing/pages/partner-billing-page/partner-billing-page.component').then(
      m => m.PartnerBillingPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.billing_admin_usage_billing_overview, () =>
    import('./prototypes/billing/pages/admin-billing-page/admin-billing-page.component').then(
      m => m.AdminBillingPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.billing_partner_usage_breakdown, () =>
    import('./prototypes/billing/pages/partner-breakdown-page/partner-breakdown-page.component').then(
      m => m.PartnerBreakdownPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.billing_admin_usage_breakdown, () =>
    import('./prototypes/billing/pages/admin-breakdown-page/admin-breakdown-page.component').then(
      m => m.AdminBreakdownPageComponent,
    ),
  ),

  // Credentials v2 prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_credentials_v2, PrototypeRouteId.credentials_v2_credentials),
  RouterTools.ofRedirect(PrototypeRouteId.credentials_v2_drp, PrototypeRouteId.credentials_v2_credentials),

  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_credentials, () =>
    import('./prototypes/credentials-v2/pages/exposed-accounts-page/exposed-accounts-page.component').then(
      m => m.ExposedAccountsPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_cookies, () =>
    import('./prototypes/credentials-v2/pages/exposed-cookies-page/exposed-cookies-page.component').then(
      m => m.ExposedCookiesPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_exposures, () =>
    import('./prototypes/credentials-v2/pages/exposure-artifacts-page/exposure-artifacts-page.component').then(
      m => m.ExposureArtifactsPageComponent,
    ),
  ),

  // Exposed account detail — redirect to overview, then sub-pages
  RouterTools.ofRedirect(
    PrototypeRouteId.credentials_v2_credential_detail,
    PrototypeRouteId.credentials_v2_credential_detail_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_credential_detail_overview, () =>
    import('./prototypes/credentials-v2/pages/exposed-account-overview-page/exposed-account-overview-page.component').then(
      m => m.ExposedAccountOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_credential_detail_scoring, () =>
    import('./prototypes/credentials-v2/pages/exposed-account-scoring-page/exposed-account-scoring-page.component').then(
      m => m.ExposedAccountScoringPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_credential_detail_activity, () =>
    import('./prototypes/credentials-v2/pages/exposed-account-activity-page/exposed-account-activity-page.component').then(
      m => m.ExposedAccountActivityPageComponent,
    ),
  ),

  // Exposed cookie detail — redirect to overview, then sub-pages
  RouterTools.ofRedirect(
    PrototypeRouteId.credentials_v2_cookie_detail,
    PrototypeRouteId.credentials_v2_cookie_detail_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_cookie_detail_overview, () =>
    import('./prototypes/credentials-v2/pages/cookie-overview-page/cookie-overview-page.component').then(
      m => m.CookieOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_cookie_detail_scoring, () =>
    import('./prototypes/credentials-v2/pages/cookie-scoring-page/cookie-scoring-page.component').then(
      m => m.CookieScoringPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_cookie_detail_activity, () =>
    import('./prototypes/credentials-v2/pages/cookie-activity-page/cookie-activity-page.component').then(
      m => m.CookieActivityPageComponent,
    ),
  ),

  // Exposure artifact detail — redirect to overview, then sub-pages
  RouterTools.ofRedirect(
    PrototypeRouteId.credentials_v2_exposure_detail,
    PrototypeRouteId.credentials_v2_exposure_detail_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_exposure_detail_overview, () =>
    import('./prototypes/credentials-v2/pages/exposure-artifact-overview-page/exposure-artifact-overview-page.component').then(
      m => m.ExposureArtifactOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_exposure_detail_scoring, () =>
    import('./prototypes/credentials-v2/pages/exposure-artifact-scoring-page/exposure-artifact-scoring-page.component').then(
      m => m.ExposureArtifactScoringPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.credentials_v2_exposure_detail_activity, () =>
    import('./prototypes/credentials-v2/pages/exposure-artifact-activity-page/exposure-artifact-activity-page.component').then(
      m => m.ExposureArtifactActivityPageComponent,
    ),
  ),

  // Cyberflex prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_cyberflex, PrototypeRouteId.cyberflex_requests_overview),
  RouterTools.ofRedirect(PrototypeRouteId.cyberflex_requests, PrototypeRouteId.cyberflex_requests_overview),
  RouterTools.ofRedirect(PrototypeRouteId.cyberflex_assets, PrototypeRouteId.cyberflex_assets_domains),

  // Cyberflex - Request pages
  RouterTools.ofLazy(PrototypeRouteId.cyberflex_requests_overview, () =>
    import('./prototypes/cyberflex/pages/requests-overview-page/requests-overview-page.component').then(
      m => m.RequestsOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.cyberflex_requests_engagement, () =>
    import('./prototypes/cyberflex/pages/engagement-requests-page/engagement-requests-page.component').then(
      m => m.EngagementRequestsPageComponent,
    ),
  ),

  // Cyberflex - Request detail — redirect to overview, then sub-pages
  RouterTools.ofRedirect(
    PrototypeRouteId.cyberflex_requests_detail,
    PrototypeRouteId.cyberflex_requests_detail_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.cyberflex_requests_detail_overview, () =>
    import('./prototypes/cyberflex/pages/request-detail-overview-page/request-detail-overview-page.component').then(
      m => m.RequestDetailOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.cyberflex_requests_detail_comments, () =>
    import('./prototypes/cyberflex/pages/request-detail-comments-page/request-detail-comments-page.component').then(
      m => m.RequestDetailCommentsPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.cyberflex_requests_detail_attachments, () =>
    import('./prototypes/cyberflex/pages/request-detail-attachments-page/request-detail-attachments-page.component').then(
      m => m.RequestDetailAttachmentsPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.cyberflex_requests_detail_activity, () =>
    import('./prototypes/cyberflex/pages/request-detail-activity-page/request-detail-activity-page.component').then(
      m => m.RequestDetailActivityPageComponent,
    ),
  ),

  // Cyberflex - Asset pages
  RouterTools.ofLazy(PrototypeRouteId.cyberflex_assets_domains, () =>
    import('./prototypes/cyberflex/pages/domains-page/domains-page.component').then(m => m.DomainsPageComponent),
  ),
  RouterTools.ofLazy(PrototypeRouteId.cyberflex_assets_hosts, () =>
    import('./prototypes/cyberflex/pages/hosts-page/hosts-page.component').then(m => m.HostsPageComponent),
  ),
  RouterTools.ofLazy(PrototypeRouteId.cyberflex_assets_websites, () =>
    import('./prototypes/cyberflex/pages/websites-page/websites-page.component').then(m => m.WebsitesPageComponent),
  ),

  // Account
  RouterTools.ofLazy(PrototypeRouteId.account_preferences, () =>
    import('./account/preferences-page/preferences-page.component').then(m => m.PreferencesPageComponent),
  ),
  {
    path: 'logout',
    canActivate: [AuthGuard, AuthLogoutGuard],
    component: SolErrorRoutePageComponent,
    data: {
      id: PrototypeRouteId.account_logout,
    },
  },

  // Asset Inventory prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_asset_inventory, PrototypeRouteId.asset_inventory_assets),
  RouterTools.ofLazy(PrototypeRouteId.asset_inventory_assets, () =>
    import('./prototypes/asset-inventory/pages/assets-page/assets-page.component').then(m => m.AssetsPageComponent),
  ),

  // DEV-18073 Compliance prototype — only findings/compliance is a real page;
  // the rest of the mirrored nav lands on the prototype's placeholder.
  RouterTools.ofRedirect(PrototypeRouteId.app_dev_18073_compliance, PrototypeRouteId.dev_18073_compliance_compliance),
  RouterTools.ofRedirect(
    PrototypeRouteId.dev_18073_compliance_findings,
    PrototypeRouteId.dev_18073_compliance_compliance,
  ),
  RouterTools.ofLazy(PrototypeRouteId.dev_18073_compliance_compliance, () =>
    import('./prototypes/dev-18073-compliance/pages/compliance-page/compliance-page.component').then(
      m => m.CompliancePageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.dev_18073_compliance_assets, () =>
    import('./prototypes/dev-18073-compliance/pages/assets-page/assets-page.component').then(
      m => m.AssetsPageComponent,
    ),
  ),
  // Configuration → Workflows is live (the wizard carries the new Compliance
  // assessment step); the parent and siblings stay placeholders.
  RouterTools.ofRedirect(
    PrototypeRouteId.dev_18073_compliance_configuration,
    PrototypeRouteId.dev_18073_compliance_workflows,
  ),
  RouterTools.ofLazy(PrototypeRouteId.dev_18073_compliance_workflow_new, () =>
    import('./prototypes/dev-18073-compliance/pages/workflow-wizard-page/workflow-wizard-page.component').then(
      m => m.WorkflowWizardPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.dev_18073_compliance_workflows, () =>
    import('./prototypes/dev-18073-compliance/pages/workflows-page/workflows-page.component').then(
      m => m.WorkflowsPageComponent,
    ),
  ),
  ...[
    PrototypeRouteId.dev_18073_compliance_asset_groups,
    PrototypeRouteId.dev_18073_compliance_vulnerabilities,
    PrototypeRouteId.dev_18073_compliance_informational,
    PrototypeRouteId.dev_18073_compliance_catalog,
    PrototypeRouteId.dev_18073_compliance_scan_configurations,
    PrototypeRouteId.dev_18073_compliance_rc_scanners,
    PrototypeRouteId.dev_18073_compliance_scan_policies,
    PrototypeRouteId.dev_18073_compliance_view_templates,
    PrototypeRouteId.dev_18073_compliance_credentials,
    PrototypeRouteId.dev_18073_compliance_integrations,
    PrototypeRouteId.dev_18073_compliance_notification_settings,
    PrototypeRouteId.dev_18073_compliance_automation,
    PrototypeRouteId.dev_18073_compliance_scans,
    PrototypeRouteId.dev_18073_compliance_report_library,
    PrototypeRouteId.dev_18073_compliance_tags,
    PrototypeRouteId.dev_18073_compliance_subscriptions,
    PrototypeRouteId.dev_18073_compliance_consumption,
    PrototypeRouteId.dev_18073_compliance_appliances,
  ].map(routeId =>
    RouterTools.ofLazy(routeId, () =>
      import('./prototypes/dev-18073-compliance/pages/placeholder-page/placeholder-page.component').then(
        m => m.Dev18073PlaceholderPageComponent,
      ),
    ),
  ),

  // Engagement requests finetuning prototype
  RouterTools.ofRedirect(
    PrototypeRouteId.app_engagement_requests_finetuning,
    PrototypeRouteId.engagement_requests_finetuning_page,
  ),
  RouterTools.ofLazy(PrototypeRouteId.engagement_requests_finetuning_page, () =>
    import('./prototypes/engagement-requests-finetuning/pages/engagement-requests-finetuning-page/engagement-requests-finetuning-page.component').then(
      m => m.EngagementRequestsFinetuningPageComponent,
    ),
  ),

  // Finding templates prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_finding_templates, PrototypeRouteId.finding_templates_list),
  RouterTools.ofRedirect(
    PrototypeRouteId.finding_templates_findings,
    PrototypeRouteId.finding_templates_vulnerabilities,
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_templates_vulnerabilities, () =>
    import('./prototypes/finding-templates/pages/vulnerabilities-page/vulnerabilities-page.component').then(
      m => m.VulnerabilitiesPageComponent,
    ),
  ),
  // Dead nav sections — visible in the menu but out of scope, so they land on a
  // blank placeholder instead of a working page (the navbar can't grey them out).
  RouterTools.ofLazy(PrototypeRouteId.finding_templates_informational, () =>
    import('./prototypes/finding-templates/pages/placeholder-page/placeholder-page.component').then(
      m => m.FindingTemplatesPlaceholderPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_templates_compliance, () =>
    import('./prototypes/finding-templates/pages/placeholder-page/placeholder-page.component').then(
      m => m.FindingTemplatesPlaceholderPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_templates_catalog, () =>
    import('./prototypes/finding-templates/pages/placeholder-page/placeholder-page.component').then(
      m => m.FindingTemplatesPlaceholderPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_templates_list, () =>
    import('./prototypes/finding-templates/pages/finding-templates-page/finding-templates-page.component').then(
      m => m.FindingTemplatesPageComponent,
    ),
  ),

  // Observation attachments prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_observation_attachments, PrototypeRouteId.observation_attachments_list),
  RouterTools.ofLazy(PrototypeRouteId.observation_attachments_list, () =>
    import('./prototypes/observation-attachments/pages/observations-list-page/observations-list-page.component').then(
      m => m.ObservationsListPageComponent,
    ),
  ),

  // Legacy observation attachments prototype (dialog flow)
  RouterTools.ofRedirect(
    PrototypeRouteId.app_legacy_observation_attachments,
    PrototypeRouteId.legacy_observation_attachments_launcher,
  ),
  RouterTools.ofLazy(PrototypeRouteId.legacy_observation_attachments_launcher, () =>
    import('./prototypes/legacy-observation-attachments/pages/observations-launcher-page/observations-launcher-page.component').then(
      m => m.ObservationsLauncherPageComponent,
    ),
  ),

  // DEV-18689 Ghostlab matches prototype — single canvas, the two finding detail
  // panels (customer read + GhostLabs edit) side by side.
  RouterTools.ofRedirect(
    PrototypeRouteId.app_dev_18689_ghostlab_matches,
    PrototypeRouteId.dev_18689_ghostlab_matches_finding,
  ),
  RouterTools.ofLazy(PrototypeRouteId.dev_18689_ghostlab_matches_finding, () =>
    import('./prototypes/dev-18689-ghostlab-matches/pages/finding-comparison-page/finding-comparison-page.component').then(
      m => m.FindingComparisonPageComponent,
    ),
  ),

  // Project North Star prototype
  RouterTools.ofRedirect(
    PrototypeRouteId.app_project_north_star,
    PrototypeRouteId.project_north_star_integrated_people_view,
  ),
  RouterTools.ofLazy(PrototypeRouteId.project_north_star_integrated_people_view, () =>
    import('./prototypes/easm-platform/project-north-star/pages/integrated-people-view-page/integrated-people-view-page.component').then(
      m => m.IntegratedPeopleViewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.project_north_star_person_detail, () =>
    import('./prototypes/easm-platform/project-north-star/pages/integrated-person-detail-page/integrated-person-detail-page.component').then(
      m => m.IntegratedPersonDetailPageComponent,
    ),
  ),
  // Vulnerability catalog prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_vulnerability_catalog, PrototypeRouteId.vulnerability_catalog_dashboard),
  RouterTools.ofLazy(PrototypeRouteId.vulnerability_catalog_dashboard, () =>
    import('./prototypes/vulnerability-catalog/pages/cve-catalog-dashboard-page/cve-catalog-dashboard-page.component').then(
      m => m.CveCatalogDashboardPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.vulnerability_catalog_list, () =>
    import('./prototypes/vulnerability-catalog/pages/cve-catalog-table-page/cve-catalog-table-page.component').then(
      m => m.CveCatalogTablePageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.vulnerability_catalog_detail, () =>
    import('./prototypes/vulnerability-catalog/pages/cve-detail-page/cve-detail-page.component').then(
      m => m.CveDetailPageComponent,
    ),
  ),

  // Dashboard banners prototype (issue 9194)
  RouterTools.ofRedirect(PrototypeRouteId.app_dashboard_banners, PrototypeRouteId.dashboard_banners_dashboard),
  RouterTools.ofLazy(PrototypeRouteId.dashboard_banners_dashboard, () =>
    import('./prototypes/easm-platform/dashboard-banners/pages/banner-dashboard-page/banner-dashboard-page.component').then(
      m => m.BannerDashboardPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.dashboard_banners_admin, () =>
    import('./prototypes/easm-platform/dashboard-banners/pages/banner-admin-page/banner-admin-page.component').then(
      m => m.BannerAdminPageComponent,
    ),
  ),

  // Subscope hierarchy prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_subscope_hierarchy, PrototypeRouteId.subscope_hierarchy_breadcrumb),
  RouterTools.ofLazy(PrototypeRouteId.subscope_hierarchy_breadcrumb, () =>
    import('./prototypes/subscope-hierarchy/pages/subscope-hierarchy-page/subscope-hierarchy-page.component').then(
      m => m.SubscopeHierarchyPageComponent,
    ),
  ),
  RouterTools.ofRedirect(PrototypeRouteId.subscope_hierarchy_scope, PrototypeRouteId.subscope_hierarchy_scope_overview),
  RouterTools.ofLazy(PrototypeRouteId.subscope_hierarchy_scope_overview, () =>
    import('./prototypes/subscope-hierarchy/pages/scope-overview-page/scope-overview-page.component').then(
      m => m.ScopeOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.subscope_hierarchy_scope_subscopes, () =>
    import('./prototypes/subscope-hierarchy/pages/scope-subscopes-page/scope-subscopes-page.component').then(
      m => m.ScopeSubscopesPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.subscope_hierarchy_scope_seeds, () =>
    import('./prototypes/subscope-hierarchy/pages/scope-seeds-page/scope-seeds-page.component').then(
      m => m.ScopeSeedsPageComponent,
    ),
  ),

  // Module read-only banner prototype
  RouterTools.ofRedirect(
    PrototypeRouteId.app_module_read_only_banner,
    PrototypeRouteId.module_read_only_banner_exposed_accounts,
  ),
  RouterTools.ofRedirect(
    PrototypeRouteId.module_read_only_banner_drp,
    PrototypeRouteId.module_read_only_banner_exposed_accounts,
  ),
  RouterTools.ofLazy(PrototypeRouteId.module_read_only_banner_exposed_accounts, () =>
    import('./prototypes/module-read-only-banner/pages/exposed-accounts-page/exposed-accounts-page.component').then(
      m => m.ExposedAccountsPageComponent,
    ),
  ),

  // Finding verification flow prototype
  RouterTools.ofRedirect(
    PrototypeRouteId.app_finding_verification_flow,
    PrototypeRouteId.finding_verification_flow_customer,
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_verification_flow_customer, () =>
    import('./prototypes/finding-verification-flow/pages/customer-findings-page/customer-findings-page.component').then(
      m => m.CustomerFindingsPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_verification_flow_pentester, () =>
    import('./prototypes/finding-verification-flow/pages/pentester-queue-page/pentester-queue-page.component').then(
      m => m.PentesterQueuePageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_verification_flow_customer_detail, () =>
    import('./prototypes/finding-verification-flow/pages/finding-detail-page/finding-detail-page.component').then(
      m => m.FindingDetailPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_verification_flow_pentester_detail, () =>
    import('./prototypes/finding-verification-flow/pages/finding-detail-page/finding-detail-page.component').then(
      m => m.FindingDetailPageComponent,
    ),
  ),

  // Finding verification flow — Engagements section
  RouterTools.ofRedirect(
    PrototypeRouteId.finding_verification_flow_engagements,
    PrototypeRouteId.finding_verification_flow_engagements_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_verification_flow_engagements_overview, () =>
    import('./prototypes/finding-verification-flow/pages/engagements-overview-page/engagements-overview-page.component').then(
      m => m.EngagementsOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_verification_flow_engagements_list, () =>
    import('./prototypes/finding-verification-flow/pages/engagements-list-page/engagements-list-page.component').then(
      m => m.EngagementsListPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_verification_flow_engagements_accounts, () =>
    import('./prototypes/finding-verification-flow/pages/engagement-accounts-page/engagement-accounts-page.component').then(
      m => m.EngagementAccountsPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.finding_verification_flow_engagement_detail, () =>
    import('./prototypes/finding-verification-flow/pages/engagement-detail-page/engagement-detail-page.component').then(
      m => m.EngagementDetailPageComponent,
    ),
  ),

  // Redirect duplicates prototype
  RouterTools.ofLazy(PrototypeRouteId.app_redirect_duplicates, () =>
    import('./prototypes/redirect-duplicates/pages/redirect-tracker-page/redirect-tracker-page.component').then(
      m => m.RedirectTrackerPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.redirect_duplicates_observation, () =>
    import('./prototypes/redirect-duplicates/pages/redirect-observation-page/redirect-observation-page.component').then(
      m => m.RedirectObservationPageComponent,
    ),
  ),
  RouterTools.ofRedirect(
    PrototypeRouteId.redirect_duplicates_websites,
    'redirect-duplicates/websites/www.lifeline.bbcmediaaction.org/overview',
  ),
  RouterTools.ofRedirect(
    PrototypeRouteId.redirect_duplicates_website,
    PrototypeRouteId.redirect_duplicates_website_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.redirect_duplicates_website_overview, () =>
    import('./prototypes/redirect-duplicates/pages/redirect-website-overview-page/redirect-website-overview-page.component').then(
      m => m.RedirectWebsiteOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.redirect_duplicates_website_findings, () =>
    import('./prototypes/redirect-duplicates/pages/redirect-findings-page/redirect-findings-page.component').then(
      m => m.RedirectWebsiteFindingsPageComponent,
    ),
  ),
  RouterTools.ofRedirect(
    PrototypeRouteId.redirect_duplicates_domains,
    'redirect-duplicates/domains/bbcmediaaction.org/overview',
  ),
  RouterTools.ofRedirect(
    PrototypeRouteId.redirect_duplicates_domain,
    PrototypeRouteId.redirect_duplicates_domain_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.redirect_duplicates_domain_overview, () =>
    import('./prototypes/redirect-duplicates/pages/redirect-domain-overview-page/redirect-domain-overview-page.component').then(
      m => m.RedirectDomainOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.redirect_duplicates_domain_findings, () =>
    import('./prototypes/redirect-duplicates/pages/redirect-findings-page/redirect-findings-page.component').then(
      m => m.RedirectDomainFindingsPageComponent,
    ),
  ),

  // Threat Explorer prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_threat_explorer, PrototypeRouteId.threat_explorer_lists),
  RouterTools.ofLazy(PrototypeRouteId.threat_explorer_lists, () =>
    import('./prototypes/threat-explorer/pages/software-lists-page/software-lists-page.component').then(
      m => m.SoftwareListsPageComponent,
    ),
  ),
  RouterTools.ofRedirect(PrototypeRouteId.threat_explorer_list, PrototypeRouteId.threat_explorer_list_detail),
  RouterTools.ofLazy(PrototypeRouteId.threat_explorer_list_detail, () =>
    import('./prototypes/threat-explorer/pages/list-detail-page/list-detail-page.component').then(
      m => m.ListDetailPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.threat_explorer_list_software, () =>
    import('./prototypes/threat-explorer/pages/list-software-page/list-software-page.component').then(
      m => m.ListSoftwarePageComponent,
    ),
  ),
  RouterTools.ofRedirect(
    PrototypeRouteId.threat_explorer_software_detail,
    PrototypeRouteId.threat_explorer_software_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.threat_explorer_software_overview, () =>
    import('./prototypes/threat-explorer/pages/software-detail-page/software-detail-page.component').then(
      m => m.SoftwareDetailPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.threat_explorer_software_activity, () =>
    import('./prototypes/threat-explorer/pages/software-activity-page/software-activity-page.component').then(
      m => m.SoftwareActivityPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.threat_explorer_list_vulnerabilities, () =>
    import('./prototypes/threat-explorer/pages/list-vulnerabilities-page/list-vulnerabilities-page.component').then(
      m => m.ListVulnerabilitiesPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.threat_explorer_vulnerability_detail, () =>
    import('./prototypes/threat-explorer/pages/vulnerability-detail-page/vulnerability-detail-page.component').then(
      m => m.VulnerabilityDetailPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.threat_explorer_list_activity, () =>
    import('./prototypes/threat-explorer/pages/list-activity-page/list-activity-page.component').then(
      m => m.ListActivityPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.threat_explorer_list_notifications, () =>
    import('./prototypes/threat-explorer/pages/list-notifications-page/list-notifications-page.component').then(
      m => m.ListNotificationsPageComponent,
    ),
  ),
  RouterTools.ofExternal(
    PrototypeRouteId.threat_explorer_platform_notifications,
    '{{LEGACY_ASM_PLATFORM_URL}}/notifications',
    [],
  ),

  ...aiAssistantRoutes,

  // Web Content v2 prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_webcontent_v2, PrototypeRouteId.webcontent_v2_dark_web),
  RouterTools.ofRedirect(PrototypeRouteId.webcontent_v2_drp, PrototypeRouteId.webcontent_v2_dark_web),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_dark_web, () =>
    import('./prototypes/webcontent-v2/pages/dark-web-page/dark-web-page.component').then(m => m.DarkWebPageComponent),
  ),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_data_leakage, () =>
    import('./prototypes/webcontent-v2/pages/data-leakage-page/data-leakage-page.component').then(
      m => m.DataLeakagePageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_social_media, () =>
    import('./prototypes/webcontent-v2/pages/social-media-page/social-media-page.component').then(
      m => m.SocialMediaPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_mobile_apps, () =>
    import('./prototypes/webcontent-v2/pages/mobile-apps-page/mobile-apps-page.component').then(
      m => m.MobileAppsPageComponent,
    ),
  ),

  RouterTools.ofRedirect(
    PrototypeRouteId.webcontent_v2_dark_web_detail,
    PrototypeRouteId.webcontent_v2_dark_web_detail_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_dark_web_detail_overview, () =>
    import('./prototypes/webcontent-v2/pages/dark-web-detail-page/dark-web-detail-overview-page/dark-web-detail-overview-page.component').then(
      m => m.DarkWebDetailOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_dark_web_detail_activity, () =>
    import('./prototypes/webcontent-v2/pages/dark-web-detail-page/dark-web-detail-activity-page/dark-web-detail-activity-page.component').then(
      m => m.DarkWebDetailActivityPageComponent,
    ),
  ),

  RouterTools.ofRedirect(
    PrototypeRouteId.webcontent_v2_data_leakage_detail,
    PrototypeRouteId.webcontent_v2_data_leakage_detail_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_data_leakage_detail_overview, () =>
    import('./prototypes/webcontent-v2/pages/data-leakage-detail-page/data-leakage-detail-overview-page/data-leakage-detail-overview-page.component').then(
      m => m.DataLeakageDetailOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_data_leakage_detail_activity, () =>
    import('./prototypes/webcontent-v2/pages/data-leakage-detail-page/data-leakage-detail-activity-page/data-leakage-detail-activity-page.component').then(
      m => m.DataLeakageDetailActivityPageComponent,
    ),
  ),

  RouterTools.ofRedirect(
    PrototypeRouteId.webcontent_v2_social_media_detail,
    PrototypeRouteId.webcontent_v2_social_media_detail_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_social_media_detail_overview, () =>
    import('./prototypes/webcontent-v2/pages/social-media-detail-page/social-media-detail-overview-page/social-media-detail-overview-page.component').then(
      m => m.SocialMediaDetailOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_social_media_detail_activity, () =>
    import('./prototypes/webcontent-v2/pages/social-media-detail-page/social-media-detail-activity-page/social-media-detail-activity-page.component').then(
      m => m.SocialMediaDetailActivityPageComponent,
    ),
  ),

  RouterTools.ofRedirect(
    PrototypeRouteId.webcontent_v2_mobile_app_detail,
    PrototypeRouteId.webcontent_v2_mobile_app_detail_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_mobile_app_detail_overview, () =>
    import('./prototypes/webcontent-v2/pages/mobile-apps-detail-page/mobile-apps-detail-overview-page/mobile-apps-detail-overview-page.component').then(
      m => m.MobileAppsDetailOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.webcontent_v2_mobile_app_detail_activity, () =>
    import('./prototypes/webcontent-v2/pages/mobile-apps-detail-page/mobile-apps-detail-activity-page/mobile-apps-detail-activity-page.component').then(
      m => m.MobileAppsDetailActivityPageComponent,
    ),
  ),

  // Asset details
  RouterTools.ofRedirect(PrototypeRouteId.app_asset_details, 'asset-details/certificates/outpost24.com/overview'),
  RouterTools.ofRedirect(PrototypeRouteId.asset_details_assets, 'asset-details/certificates/outpost24.com/overview'),
  RouterTools.ofRedirect(
    PrototypeRouteId.asset_details_certificates,
    'asset-details/certificates/outpost24.com/overview',
  ),
  RouterTools.ofRedirect(
    PrototypeRouteId.asset_details_certificate,
    PrototypeRouteId.asset_details_certificate_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_certificate_overview, () =>
    import('./prototypes/asset-details/pages/certificate-overview-page/certificate-overview-page.component').then(
      m => m.CertificateOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_certificate_findings, () =>
    import('./prototypes/asset-details/pages/asset-findings-page/asset-findings-page.component').then(
      m => m.CertificateFindingsPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_certificate_scoring, () =>
    import('./prototypes/asset-details/pages/asset-scoring-page/asset-scoring-page.component').then(
      m => m.CertificateScoringPageComponent,
    ),
  ),
  RouterTools.ofRedirect(
    PrototypeRouteId.asset_details_domain_discovery,
    'asset-details/domain-discovery/0utpost24.com/overview',
  ),
  RouterTools.ofRedirect(
    PrototypeRouteId.asset_details_domain_candidate,
    PrototypeRouteId.asset_details_domain_candidate_overview,
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_domain_candidate_overview, () =>
    import('./prototypes/asset-details/pages/domain-candidate-page/domain-candidate-page.component').then(
      m => m.DomainCandidatePageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_domain_candidate_activity, () =>
    import('./prototypes/asset-details/pages/domain-candidate-page/domain-candidate-page.component').then(
      m => m.DomainCandidateActivityPageComponent,
    ),
  ),
  RouterTools.ofRedirect(PrototypeRouteId.asset_details_domains, 'asset-details/domains/blog.outpost24.com/overview'),
  RouterTools.ofRedirect(PrototypeRouteId.asset_details_domain, PrototypeRouteId.asset_details_domain_overview),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_domain_overview, () =>
    import('./prototypes/asset-details/pages/domain-overview-page/domain-overview-page.component').then(
      m => m.DomainOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_domain_findings, () =>
    import('./prototypes/asset-details/pages/asset-findings-page/asset-findings-page.component').then(
      m => m.DomainFindingsPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_domain_scoring, () =>
    import('./prototypes/asset-details/pages/asset-scoring-page/asset-scoring-page.component').then(
      m => m.DomainScoringPageComponent,
    ),
  ),
  RouterTools.ofRedirect(PrototypeRouteId.asset_details_hosts, 'asset-details/hosts/192.0.2.10/overview'),
  RouterTools.ofRedirect(PrototypeRouteId.asset_details_host, PrototypeRouteId.asset_details_host_overview),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_host_overview, () =>
    import('./prototypes/asset-details/pages/host-overview-page/host-overview-page.component').then(
      m => m.HostOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_host_findings, () =>
    import('./prototypes/asset-details/pages/asset-findings-page/asset-findings-page.component').then(
      m => m.HostFindingsPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_host_scoring, () =>
    import('./prototypes/asset-details/pages/asset-scoring-page/asset-scoring-page.component').then(
      m => m.HostScoringPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_host_scan_history, () =>
    import('./prototypes/asset-details/pages/host-scan-history-page/host-scan-history-page.component').then(
      m => m.HostScanHistoryPageComponent,
    ),
  ),
  RouterTools.ofRedirect(PrototypeRouteId.asset_details_websites, 'asset-details/websites/www.outpost24.com/overview'),
  RouterTools.ofRedirect(PrototypeRouteId.asset_details_website, PrototypeRouteId.asset_details_website_overview),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_website_overview, () =>
    import('./prototypes/asset-details/pages/website-overview-page/website-overview-page.component').then(
      m => m.WebsiteOverviewPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_website_findings, () =>
    import('./prototypes/asset-details/pages/asset-findings-page/asset-findings-page.component').then(
      m => m.WebsiteFindingsPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.asset_details_website_scoring, () =>
    import('./prototypes/asset-details/pages/asset-scoring-page/asset-scoring-page.component').then(
      m => m.WebsiteScoringPageComponent,
    ),
  ),

  // Workflows prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_workflows, PrototypeRouteId.workflows_tracker),
  RouterTools.ofLazy(PrototypeRouteId.workflows_tracker, () =>
    import('./prototypes/workflows/pages/tracker-workflow-page/tracker-workflow-page.component').then(
      m => m.TrackerWorkflowPageComponent,
    ),
  ),
  RouterTools.ofRedirect(PrototypeRouteId.workflows_drp, PrototypeRouteId.workflows_dark_web),
  RouterTools.ofLazy(PrototypeRouteId.workflows_dark_web, () =>
    import('./prototypes/workflows/pages/dark-web-workflow-page/dark-web-workflow-page.component').then(
      m => m.DarkWebWorkflowPageComponent,
    ),
  ),
  RouterTools.ofRedirect(PrototypeRouteId.workflows_assets, PrototypeRouteId.workflows_domain_discovery),
  RouterTools.ofLazy(PrototypeRouteId.workflows_domain_discovery, () =>
    import('./prototypes/workflows/pages/domains-workflow-page/domains-workflow-page.component').then(
      m => m.DomainsWorkflowPageComponent,
    ),
  ),

  // Annotation notes prototype
  RouterTools.ofRedirect(PrototypeRouteId.app_annotation_notes, PrototypeRouteId.annotation_notes_tracker),
  RouterTools.ofLazy(PrototypeRouteId.annotation_notes_tracker, () =>
    import('./prototypes/annotation-notes/pages/notes-tracker-page/notes-tracker-page.component').then(
      m => m.NotesTrackerPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.annotation_notes_tracker_overview, () =>
    import('./prototypes/annotation-notes/pages/notes-finding-page/notes-finding-page.component').then(
      m => m.NotesFindingPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.annotation_notes_exposed_accounts, () =>
    import('./prototypes/annotation-notes/pages/notes-accounts-page/notes-accounts-page.component').then(
      m => m.NotesAccountsPageComponent,
    ),
  ),
  RouterTools.ofLazy(PrototypeRouteId.annotation_notes_exposed_account_overview, () =>
    import('./prototypes/annotation-notes/pages/notes-account-page/notes-account-page.component').then(
      m => m.NotesAccountPageComponent,
    ),
  ),
];
